import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  CircularProgress,
  Grid,
  Collapse,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  ContentCopy as CloneIcon,
  GroupAdd as GroupAddIcon,
  RemoveCircleOutline as RemoveIcon,
  Search as SearchIcon,
  Cloud as CloudIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getTrail, deleteTrail, listCloudTrailAccounts, addTrailAccounts, removeTrailAccounts } from '../../../services/portalApi';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import {
  PortalLoading,
  PortalErrorState,
  PortalSectionHeader,
} from '../../../components/portal/PortalStates';
import { formatStatusLabel } from '../../../utils/statusLabel';
import {
  CK,
  FONT_BODY,
  statusChipSx,
  tableHeadRowSx,
  headCellSx,
  rowSx,
  bodyCellSx,
  sectionCardSx,
  listCardSx,
  insetPanelSx,
  emptyTextSx,
  actionRowSx,
  dialogProps,
  dialogTitleSx, tileEntranceStyle
} from '../portalStyles';

const statusIntent = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'error';
  if (status === 'partial_failure') return 'warning';
  if (status === 'pending' || status === 'deleted') return 'neutral';
  return 'info';
};

// Equal-height label/value tile so the metadata grid never reads as ragged.
const MetaTile = ({ label, value }) => (
  <Box sx={{ ...insetPanelSx, p: 2, height: '100%' }}>
    <Typography
      sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', fontWeight: 500, color: CK.muted, mb: 0.5 }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', fontWeight: 600, color: CK.navy, wordBreak: 'break-word' }}
    >
      {value}
    </Typography>
  </Box>
);

const dialogButtonSx = {
  px: 3,
  '&.Mui-disabled': { backgroundColor: CK.border, color: CK.muted },
};

const TrailDetail = () => {
  const { trailId } = useParams();
  const navigate = useNavigate();
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedErrors, setExpandedErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteBucket, setDeleteBucket] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addAccountsDialogOpen, setAddAccountsDialogOpen] = useState(false);
  const [removeAccountDialogOpen, setRemoveAccountDialogOpen] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState(null);
  const [realmAccounts, setRealmAccounts] = useState([]);
  const [selectedNewAccounts, setSelectedNewAccounts] = useState([]);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [addingAccounts, setAddingAccounts] = useState(false);
  const [removingAccount, setRemovingAccount] = useState(false);
  const pollRef = useRef(null);

  const fetchTrail = useCallback(async () => {
    try {
      const response = await getTrail(trailId);
      setTrail(response.data);
      setError(null);
      return response.data;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [trailId]);

  useEffect(() => {
    fetchTrail();
  }, [fetchTrail]);

  // Poll while any account is still in progress
  useEffect(() => {
    if (!trail) return;

    const activeStatuses = ['pending', 'escalating', 'creating_bucket', 'configuring_bucket', 'creating_trail', 'cleaning_up', 'deleting_trail', 'deleting_bucket', 'updating_trail', 'adding_trail', 'removing_trail'];
    const hasInProgress = trail.accountStatuses?.some((s) =>
      activeStatuses.includes(s.status)
    );

    if (hasInProgress) {
      pollRef.current = setInterval(async () => {
        const data = await fetchTrail();
        if (!data) {
          // Trail was hard-deleted from DB — navigate to list
          clearInterval(pollRef.current);
          pollRef.current = null;
          navigate('/cloudtrail');
          return;
        }
        const stillInProgress = data.accountStatuses?.some((s) =>
          activeStatuses.includes(s.status)
        );
        if (!stillInProgress && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 5000);
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [trail, fetchTrail]);

  const toggleError = (accountId) => {
    setExpandedErrors((prev) => ({ ...prev, [accountId]: !prev[accountId] }));
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteTrail(trailId, deleteBucket);
      setDeleteDialogOpen(false);
      setDeleteBucket(false);
      // Refresh to show "deleting" status — polling will pick up the rest
      const data = await fetchTrail();
      if (!data) {
        navigate('/cloudtrail');
      }
    } catch (err) {
      setError(err.message);
      setDeleteDialogOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const isTrailBusy = trail && (trail.status === 'in_progress' || trail.status === 'deleting' || trail.status === 'updating');

  const handleOpenAddAccounts = async () => {
    try {
      const response = await listCloudTrailAccounts();
      const allAccounts = response.data?.accounts || [];
      const currentIds = trail.targetAccountIds || [];
      const available = allAccounts.filter((a) => !currentIds.includes(a.accountId));
      setRealmAccounts(available);
      setSelectedNewAccounts([]);
      setAccountSearchQuery('');
      setAddAccountsDialogOpen(true);
    } catch (err) {
      toast.error(`Failed to load accounts: ${err.message}`);
    }
  };

  const handleAddAccountsConfirm = async () => {
    try {
      setAddingAccounts(true);
      await addTrailAccounts(trailId, selectedNewAccounts);
      setAddAccountsDialogOpen(false);
      setSelectedNewAccounts([]);
      setAccountSearchQuery('');
      toast.success('Account addition started');
      fetchTrail();
    } catch (err) {
      toast.error(`Failed to add accounts: ${err.message}`);
    } finally {
      setAddingAccounts(false);
    }
  };

  const handleOpenRemoveAccount = (acct) => {
    setAccountToRemove(acct);
    setRemoveAccountDialogOpen(true);
  };

  const handleRemoveAccountConfirm = async () => {
    try {
      setRemovingAccount(true);
      await removeTrailAccounts(trailId, [accountToRemove.accountId]);
      setRemoveAccountDialogOpen(false);
      setAccountToRemove(null);
      toast.success('Account removal started');
      fetchTrail();
    } catch (err) {
      toast.error(`Failed to remove account: ${err.message}`);
    } finally {
      setRemovingAccount(false);
    }
  };

  const backButton = (
    <Button
      startIcon={<ArrowBackIcon />}
      onClick={() => navigate('/cloudtrail')}
      sx={{ color: CK.body, fontFamily: FONT_BODY, fontWeight: 600, px: 1.5, mb: 2 }}
    >
      Back to Trails
    </Button>
  );

  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          {backButton}
          <PortalPageHeader
            icon={<CloudIcon />}
            title="Trail"
            subtitle="Loading trail details"
            color="#4698D3"
          />
        </motion.div>

        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading trail…" />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }

  if (error || !trail) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>
          {backButton}
          <PortalPageHeader
            icon={<CloudIcon />}
            title="Trail"
            subtitle="This trail could not be loaded"
            color="#4698D3"
          />
        </motion.div>

        <motion.div variants={sectionVariants}>
          <PortalErrorState
            error={error || 'Trail not found — it may have been deleted.'}
            onRetry={fetchTrail}
          />
        </motion.div>
      </motion.div>
    );
  }

  const renderEventSummary = () => {
    const ec = trail.eventConfig;
    if (!ec) return 'None configured';

    const parts = [];
    if (ec.managementEvents?.enabled) parts.push('Management');
    if (ec.dataEvents?.length > 0) parts.push(`${ec.dataEvents.length} Data`);
    if (ec.insightSelectors?.length > 0) parts.push('Insights');
    if (ec.networkActivityEvents?.length > 0) parts.push(`${ec.networkActivityEvents.length} Network`);
    return parts.length > 0 ? parts.join(', ') : 'None configured';
  };

  const accountStatuses = trail.accountStatuses || [];

  const removeTooltip = (acct) => {
    if (acct.accountId === trail.s3BucketAccountId) return 'Cannot remove the S3 bucket account';
    if (accountStatuses.length <= 1) return 'Cannot remove the last account';
    if (isTrailBusy) return 'Wait for the current operation to finish';
    return 'Remove account';
  };

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      {/* Header */}
      <motion.div variants={sectionVariants}>
        {backButton}
        <PortalPageHeader
          icon={<CloudIcon />}
          title={trail.name}
          subtitle={`${accountStatuses.length} account${accountStatuses.length !== 1 ? 's' : ''} · ${trail.region || 'us-east-1'}`}
          color="#4698D3"
          action={
            <Box sx={{ ...actionRowSx, justifyContent: 'flex-end' }}>
              <Chip
                size="small"
                label={formatStatusLabel(trail.status)}
                sx={statusChipSx(statusIntent(trail.status))}
              />
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/cloudtrail/trails/${trailId}/edit`)}
                disabled={isTrailBusy}
                sx={{ px: 2.5, '&.Mui-disabled': { backgroundColor: CK.border, color: CK.muted } }}
              >
                Edit Trail
              </Button>
            </Box>
          }
        />
      </motion.div>

      {/* Trail Metadata */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
          <PortalSectionHeader
            icon={<CloudIcon sx={{ fontSize: 20 }} />}
            title="Trail configuration"
            subtitle="Settings applied to every targeted account"
            color={CK.blue}
            action={
              <Box sx={{ ...actionRowSx, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<GroupAddIcon />}
                  onClick={handleOpenAddAccounts}
                  disabled={isTrailBusy}
                  sx={{ px: 2, color: CK.blue, borderColor: `${CK.blue}40` }}
                >
                  Add Accounts
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CloneIcon />}
                  onClick={() => navigate(`/cloudtrail/trails/create?cloneFrom=${trailId}`)}
                  disabled={isTrailBusy}
                  sx={{ px: 2, color: CK.blue, borderColor: `${CK.blue}40` }}
                >
                  Clone Trail
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isTrailBusy}
                  sx={{ px: 2, borderColor: `${CK.red}40` }}
                >
                  Delete Trail
                </Button>
              </Box>
            }
          />

          {trail.errorMessage && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                borderRadius: '8px',
                fontFamily: FONT_BODY,
                '& .MuiAlert-message': { fontFamily: FONT_BODY, wordBreak: 'break-word' },
              }}
            >
              {trail.errorMessage}
            </Alert>
          )}

          {/* No motion wrapper: the tiles animate themselves in pure CSS, and a
              motion component here can write an inline opacity onto them once
              its own animation settles. */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div style={tileEntranceStyle(0)}>
                  <MetaTile label="S3 Bucket" value={trail.s3BucketName} />
                </div>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div style={tileEntranceStyle(1)}>
                  <MetaTile label="Multi-Region" value={trail.isMultiRegion ? 'Yes' : 'No'} />
                </div>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div style={tileEntranceStyle(2)}>
                  <MetaTile label="Region" value={trail.region || 'us-east-1'} />
                </div>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div style={tileEntranceStyle(3)}>
                  <MetaTile label="Events" value={renderEventSummary()} />
                </div>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div style={tileEntranceStyle(4)}>
                  <MetaTile label="Created By" value={trail.createdByEmail} />
                </div>
              </Grid>
              {trail.s3Prefix && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <div style={tileEntranceStyle(5)}>
                    <MetaTile label="S3 Prefix" value={trail.s3Prefix} />
                  </div>
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div style={tileEntranceStyle(6)}>
                  <MetaTile label="Created At" value={new Date(trail.createdAt).toLocaleString()} />
                </div>
              </Grid>
            </Grid>
        </Paper>
      </motion.div>

      {/* Per-Account Status */}
      <motion.div variants={sectionVariants}>
        <Paper elevation={0} sx={listCardSx}>
          <Box sx={{ p: 3, pb: 2 }}>
            <PortalSectionHeader
              icon={<PeopleIcon sx={{ fontSize: 20 }} />}
              title="Account status"
              subtitle={`${accountStatuses.length} account${accountStatuses.length !== 1 ? 's' : ''} targeted by this trail`}
              color={CK.blue}
              mb={0}
            />
          </Box>

          <TableContainer sx={{ borderTop: `1px solid ${CK.border}` }}>
            <Table>
              <TableHead>
                <TableRow sx={tableHeadRowSx}>
                  {['Account', 'Status', 'Phase', 'Trail ARN', 'Error', 'Updated', 'Actions'].map((header) => (
                    <TableCell key={header} sx={headCellSx}>
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {accountStatuses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                      <Typography sx={emptyTextSx}>
                        No accounts are targeted by this trail yet — use Add Accounts to roll it out.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  accountStatuses.map((acct, index) => {
                    return (
                      <React.Fragment key={acct.accountId}>
                        <TableRow sx={{ ...rowSx(index), transition: 'background-color 0.2s ease, border-color 0.2s ease' }}>
                          <TableCell sx={bodyCellSx}>
                            <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.875rem' }}>
                              {acct.accountName}
                            </Typography>
                            <Typography sx={{ fontFamily: FONT_BODY, color: CK.muted, fontSize: '0.75rem' }}>
                              {acct.accountId}
                            </Typography>
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Chip
                              size="small"
                              label={formatStatusLabel(acct.status)}
                              sx={statusChipSx(statusIntent(acct.status))}
                            />
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, color: CK.body }}>
                            {acct.phase}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, color: CK.body, fontSize: '0.75rem', maxWidth: 200, wordBreak: 'break-all' }}>
                            {acct.trailArn || '-'}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            {acct.errorMessage ? (
                              <Box>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleError(acct.accountId)}
                                  sx={{
                                    color: CK.body,
                                    borderRadius: '6px',
                                    transition: 'background-color 0.2s ease, color 0.2s ease',
                                    '&:hover': { backgroundColor: CK.hover, color: CK.navy },
                                  }}
                                >
                                  {expandedErrors[acct.accountId] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                </IconButton>
                                <Collapse in={expandedErrors[acct.accountId]}>
                                  <Typography
                                    sx={{ fontFamily: FONT_BODY, color: CK.red, fontSize: '0.75rem', mt: 0.5, maxWidth: 300, wordBreak: 'break-word' }}
                                  >
                                    {acct.errorMessage}
                                  </Typography>
                                </Collapse>
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell sx={{ ...bodyCellSx, color: CK.body, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {new Date(acct.updatedAt).toLocaleTimeString()}
                          </TableCell>
                          <TableCell sx={bodyCellSx}>
                            <Tooltip title={removeTooltip(acct)}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenRemoveAccount(acct)}
                                  disabled={
                                    isTrailBusy ||
                                    acct.accountId === trail.s3BucketAccountId ||
                                    (trail.accountStatuses?.length || 0) <= 1
                                  }
                                  sx={{
                                    color: CK.red,
                                    borderRadius: '6px',
                                    transition: 'background-color 0.2s ease',
                                    '&:hover': { backgroundColor: '#FDF2F2' },
                                    '&.Mui-disabled': { color: CK.disabled },
                                  }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </motion.div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setDeleteBucket(false); }}
        {...dialogProps}
      >
        <DialogTitle sx={dialogTitleSx}>
          Delete Trail
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              This will delete the trail <strong>{trail.name}</strong> from all{' '}
              {accountStatuses.length} AWS accounts. This action cannot be undone.
            </Typography>
            {trail.s3BucketIsNew && (
              <Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={deleteBucket}
                      onChange={(e) => setDeleteBucket(e.target.checked)}
                      color="error"
                    />
                  }
                  label={
                    <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.navy, wordBreak: 'break-word' }}>
                      Also delete S3 bucket <strong>{trail.s3BucketName}</strong>
                    </Typography>
                  }
                />
                {deleteBucket && (
                  <Typography sx={{ fontFamily: FONT_BODY, color: CK.red, fontSize: '0.8rem', ml: 4 }}>
                    All log data in this bucket will be permanently deleted.
                  </Typography>
                )}
              </Box>
            )}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => { setDeleteDialogOpen(false); setDeleteBucket(false); }}
            variant="outlined"
            sx={{ px: 3, color: CK.body, borderColor: CK.border }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            variant="contained"
            color="error"
            sx={dialogButtonSx}
          >
            {deleting ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Accounts Dialog */}
      <Dialog
        open={addAccountsDialogOpen}
        onClose={() => { setAddAccountsDialogOpen(false); setSelectedNewAccounts([]); setAccountSearchQuery(''); }}
        {...dialogProps}
      >
        <DialogTitle sx={dialogTitleSx}>
          Add Accounts
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 2 }}>
              Select accounts to add to trail <strong>{trail.name}</strong>.
            </Typography>
            {realmAccounts.length === 0 ? (
              <Typography sx={emptyTextSx}>
                No additional accounts are available to add to this trail.
              </Typography>
            ) : (
              <>
                <TextField
                  placeholder="Search accounts..."
                  value={accountSearchQuery}
                  onChange={(e) => setAccountSearchQuery(e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: CK.muted }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ maxHeight: 300, overflowY: 'auto', border: `1px solid ${CK.border}`, borderRadius: '8px' }}>
                  {realmAccounts.filter((account) => {
                    if (!accountSearchQuery.trim()) return true;
                    const q = accountSearchQuery.toLowerCase().trim();
                    return account.name.toLowerCase().includes(q) || account.accountId.includes(q);
                  }).map((account) => (
                    <Box
                      key={account.accountId}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        p: 1.5,
                        borderBottom: `1px solid ${CK.border}`,
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        '&:last-of-type': { borderBottom: 'none' },
                        '&:hover': { backgroundColor: CK.pageBg },
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSelectedNewAccounts((prev) =>
                          prev.includes(account.accountId)
                            ? prev.filter((id) => id !== account.accountId)
                            : [...prev, account.accountId]
                        )
                      }
                    >
                      <Checkbox
                        checked={selectedNewAccounts.includes(account.accountId)}
                        sx={{ mr: 1 }}
                      />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.875rem', wordBreak: 'break-word' }}>
                          {account.name}
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_BODY, color: CK.muted, fontSize: '0.75rem' }}>
                          {account.accountId}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => { setAddAccountsDialogOpen(false); setSelectedNewAccounts([]); setAccountSearchQuery(''); }}
            variant="outlined"
            sx={{ px: 3, color: CK.body, borderColor: CK.border }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddAccountsConfirm}
            disabled={addingAccounts || selectedNewAccounts.length === 0}
            variant="contained"
            sx={dialogButtonSx}
          >
            {addingAccounts ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Add Selected (${selectedNewAccounts.length})`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Account Confirmation Dialog */}
      <Dialog
        open={removeAccountDialogOpen}
        onClose={() => { setRemoveAccountDialogOpen(false); setAccountToRemove(null); }}
        {...dialogProps}
      >
        <DialogTitle sx={dialogTitleSx}>
          Remove Account
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1 }}>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body, mb: 1 }}>
              Are you sure you want to remove this account from trail <strong>{trail.name}</strong>?
            </Typography>
            {accountToRemove && (
              <Paper elevation={0} sx={{ ...insetPanelSx, p: 2, mt: 1 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, color: CK.navy, fontSize: '0.875rem', wordBreak: 'break-word' }}>
                  {accountToRemove.accountName}
                </Typography>
                <Typography sx={{ fontFamily: FONT_BODY, color: CK.muted, fontSize: '0.8rem' }}>
                  {accountToRemove.accountId}
                </Typography>
              </Paper>
            )}
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.8rem', color: CK.red, mt: 2 }}>
              The trail will be deleted from this account. This action cannot be undone.
            </Typography>
          </motion.div>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
          <Button
            onClick={() => { setRemoveAccountDialogOpen(false); setAccountToRemove(null); }}
            variant="outlined"
            sx={{ px: 3, color: CK.body, borderColor: CK.border }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveAccountConfirm}
            disabled={removingAccount}
            variant="contained"
            color="error"
            sx={dialogButtonSx}
          >
            {removingAccount ? <CircularProgress size={20} color="inherit" /> : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
};

export default TrailDetail;
